import { useCallback, useMemo } from 'react';
import { QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import request, { Variables } from 'graphql-request';
import { PageInfo } from '../services/gql/types';
import useGraphqlRequestParams from './useGraphqlRequestParams';

export type PageType<T> = {
  items: T[];
  pageInfo: PageInfo;
};

export type PagedQueryOptions<T, ResponseT> = {
  queryKey: QueryKey;
  query: string;
  variables?: Variables;
  enabled?: boolean;
  select: (data: ResponseT) => PageType<T>;
  onError?: () => void;
  envId?: string;
};

const useGraphqlPagedQuery = <T, ResponseT>({
  queryKey,
  query,
  variables,
  enabled,
  select,
  onError,
  envId,
}: PagedQueryOptions<T, ResponseT>) => {
  const { accessToken, environmentId, gqlEndpoint } = useGraphqlRequestParams({
    envId,
  });

  const { data, isFetching, isLoading, isError, fetchNextPage } =
    useInfiniteQuery<PageType<T>>({
      queryKey,
      queryFn: async ({ pageParam = null }) => {
        const queryVariables = {
          ...variables,
          cursor: pageParam,
        };
        const response = await request(gqlEndpoint, query, queryVariables, {
          authorization: `Bearer ${accessToken}`,
        });
        return select(response as ResponseT);
      },
      enabled: environmentId !== undefined && !!accessToken && enabled,
      onError: onError,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return null;
        const { pageInfo } = lastPage;
        return pageInfo.hasNextPage ? pageInfo.nextCursor : null;
      },
    });

  const flattenedData = useMemo(
    () => data?.pages.flatMap((page) => page?.items || []) || [],
    [data],
  );
  const hasNextPage = useMemo(() => {
    if (!data?.pages) return false;
    const lastPage = data?.pages[data?.pages.length - 1];
    if (!lastPage) return false;
    return lastPage.pageInfo.hasNextPage;
  }, [data]);

  const fetchAllPages = useCallback(
    async (overrideVariables?: Partial<Variables>): Promise<T[]> => {
      const allItems: T[] = [];
      let cursor: string | null = null;
      let hasMore = true;
      const seenCursors = new Set<string>();

      while (hasMore) {
        const queryVariables = {
          ...variables,
          ...overrideVariables,
          cursor,
        };
        const response = await request(gqlEndpoint, query, queryVariables, {
          authorization: `Bearer ${accessToken}`,
        });
        const page = select(response as ResponseT);
        allItems.push(...page.items);

        const { hasNextPage, nextCursor } = page.pageInfo;
        if (!hasNextPage) {
          hasMore = false;
        } else if (!nextCursor) {
          hasMore = false;
        } else if (seenCursors.has(nextCursor)) {
          hasMore = false;
        } else {
          seenCursors.add(nextCursor);
          cursor = nextCursor;
        }
      }

      return allItems;
    },
    [gqlEndpoint, query, variables, accessToken, select],
  );

  return {
    data: flattenedData,
    isFetching,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    fetchAllPages,
  };
};

export default useGraphqlPagedQuery;
